import { useFetchElementsTemplates, ElementTypeEnum, ElementsTemplateGroupedByType } from '@entities/leadCollector';
import { useState, useCallback, useMemo, ReactNode } from 'react';
import { FormElementItem } from './ui/FormElementItem';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

interface FormElementSidebarProps {
  renderContent?: (
    elementType: ElementTypeEnum,
    elements: ElementsTemplateGroupedByType['elements'],
    index: number
  ) => ReactNode;
  onElementQuickAdd?: (
    elementType: ElementTypeEnum,
    elements: ElementsTemplateGroupedByType['elements']
  ) => boolean | void;
  handlers: Record<string, (id: number) => void>;
}

export const FormElementSidebar = ({ renderContent, handlers, onElementQuickAdd }: FormElementSidebarProps) => {
  const [hoveredType, setHoveredType] = useState<ElementTypeEnum | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<ElementTypeEnum | undefined>(undefined);
  const { elementsTemplates, fetching } = useFetchElementsTemplates();

  const handleItemClick = (type: ElementTypeEnum, elements: ElementsTemplateGroupedByType['elements']) => {
    const shouldPreventToggle = onElementQuickAdd?.(type, elements);
    if (shouldPreventToggle) return;
    setSelectedType((prev) => (prev === type ? undefined : type));
  };

  const handleClose = useCallback(() => {
    setSelectedType(undefined);
  }, []);

  const skeletonItems = useMemo(() => Array.from({ length: 8 }, (_, index) => index), []);

  if (fetching) {
    return (
      <div {...stylex.props(styles.sidebar)}>
        <div {...stylex.props(styles.elementList)}>
          {skeletonItems.map((index) => (
            <div key={index} {...stylex.props(styles.skeletonItem)} />
          ))}
        </div>
      </div>
    );
  }

  const onClickElement = (group: ElementsTemplateGroupedByType) => {
    const id = group.elements[0].id;
    if (id && handlers[group.type]) {
      return handlers[group.type](id);
    }

    return handleItemClick(group.type, group.elements);
  };

  return (
    <div {...stylex.props(styles.sidebar)}>
      <div {...stylex.props(styles.elementList)}>
        {elementsTemplates.map((group, index) => (
          <FormElementItem
            key={group.type}
            group={group}
            isHovered={hoveredType === group.type}
            isSelected={selectedType === group.type}
            onMouseEnter={() => setHoveredType(group.type)}
            onMouseLeave={() => setHoveredType(undefined)}
            onClick={() => onClickElement(group)}
            onClose={handleClose}
            renderContent={renderContent}
            index={index}
          />
        ))}
      </div>
    </div>
  );
};
